import React, { useState, useRef } from 'react';
import { useLotImage } from '../../utils/getLotImage';
import { useOperationData } from './hooks/useOperationData';
import { useOperationTabs } from './hooks/useOperationTabs';
import { useUnreadCounts } from '../../services/messagerie/useUnreadCounts';
import OperationHeader from './components/OperationHeader';
import OperationTabs from './components/OperationTabs';
import OperationContent from './components/OperationContent';
import { NotFound } from '../Erreurs/Erreurs';
import './SuiviOperationBCV2.css';

// Skeleton plein ecran (chargement) : silhouette banniere + onglets + contenu, dans les
// MEMES conteneurs (.opbc / .opbc__content / .opbc__tab-content) et avec la meme geometrie
// que le vrai rendu -> aucun saut de layout a l'arrivee des donnees. Purement decoratif.
const OperationBCSkeleton: React.FC = () => (
  <div className="opbc opbc-skeleton" aria-hidden="true">
    <div className="opbc-sk-banner">
      <div className="opbc-sk-back"><span className="opbc-sk opbc-sk-circle" /></div>
      <div className="opbc-sk-h1"><span className="opbc-sk" /></div>
      <div className="opbc-sk-h2"><span className="opbc-sk" /></div>
      <div className="opbc-sk-subtitle"><span className="opbc-sk" /></div>
      <div className="opbc-sk-statusline"><span className="opbc-sk" /></div>
    </div>
    <div className="opbc__content">
      <div className="opbc-sk-tabs">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className="opbc-sk opbc-sk-tab" />
        ))}
      </div>
      <div className="opbc__tab-content">
        <div className="opbc-sk-content">
          <span className="opbc-sk opbc-sk-block" />
          <span className="opbc-sk opbc-sk-block" />
          <span className="opbc-sk opbc-sk-block opbc-sk-block--lg" />
        </div>
      </div>
    </div>
  </div>
);

// Page acheteur operation-bc (v2). Orchestration mince : data -> onglets -> rendu.
// Toute la logique vit dans les hooks ; les feuilles lourdes sont reutilisees telles quelles.
const SuiviOperationBCV2: React.FC = () => {
  const { operation, setOperation, loading, error, notFound, refetch } = useOperationData();
  const lotImage = useLotImage(operation?.lot_technique?.nom);
  const { descriptors, activeIndex, activeTab, setActiveIndex } = useOperationTabs(operation);
  // Pastille messagerie (badge onglet), cote acheteur BDC.
  const { counts: unreadCounts, markRead } = useUnreadCounts('/bdc', 'acheteur', operation?.id);

  const [acceptCGV, setAcceptCGV] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return <OperationBCSkeleton />;
  }

  // Identifiant inexistant dans l'URL -> page 404 dediee (avec navbar si connecte).
  if (notFound) {
    return <NotFound />;
  }

  if (error && !operation) {
    return (
      <div className="opbc__error">
        <p>Erreur: {error}</p>
        <p>Impossible de charger les détails de cette opération BC.</p>
      </div>
    );
  }

  if (!operation) {
    return <div className="opbc__empty">Aucune opération BC trouvée avec cet identifiant.</div>;
  }

  return (
    <div className="opbc" ref={contentRef}>
      <div className="no-print">
        <OperationHeader operation={operation} backgroundImage={lotImage} />
      </div>

      <div className="opbc__content">
        <OperationTabs descriptors={descriptors} activeIndex={activeIndex} onSelect={setActiveIndex} unreadCounts={unreadCounts} />

        <div className="opbc__tab-content">
          <OperationContent
            operation={operation}
            setOperation={setOperation}
            refetch={refetch}
            descriptors={descriptors}
            activeTab={activeTab}
            onNavigateTab={setActiveIndex}
            errors={errors}
            setErrors={setErrors}
            acceptCGV={acceptCGV}
            setAcceptCGV={setAcceptCGV}
            printRef={contentRef}
            onMarkRead={markRead}
          />
        </div>
      </div>
    </div>
  );
};

export default SuiviOperationBCV2;
